import axios from 'axios'

/**
 * @desc Fetches Performance Data during selected range dates for specified supplier
 *
 * @param {Integer} supplierId
 * @param {String} dateFromCurrent
 * @param {String} dateToCurrent
 * @param {String} dateFromPrevious
 * @param {String} dateToPrevious
 */
export const fetchCompareReports = (
  supplierId,
  dateFromCurrent,
  dateToCurrent,
  dateFromPrevious,
  dateToPrevious
) =>
  axios
    .get(
      `/authenticated/report/performance?supplierId=${supplierId}&dateFromCurrent=${dateFromCurrent}&dateToCurrent=${dateToCurrent}&dateFromPrevious=${dateFromPrevious}&dateToPrevious=${dateToPrevious}`
    )
    .then((res) => res.data)

export const fetchForecastReport = ({ includeOptionalSections } = {}) => {
  const query =
    typeof includeOptionalSections === 'boolean'
      ? `?includeOptionalSections=${includeOptionalSections}`
      : ''

  return axios
    .get(`/authenticated/report/forecast${query}`)
    .then((res) => res.data)
}

export const fetchForecastPdf = () =>
  axios
    .get('/authenticated/report/forecast/pdf', { responseType: 'blob' })
    .then((res) => ({
      blob: res.data,
      filename: extractFilename(res.headers?.['content-disposition'])
    }))

/**
 * @desc Fetches Sales By Products report data
 * @param {String} aggregationPeriod - required
 * @param {Array} bannerGroupIds
 * @param {String} dateFrom - required
 * @param {String} dateTo - required
 * @param {Array} deliveryAddressStates
 * @param {Array} multiStoreOperatorIds
 * @param {Array} storeIds
 * @param {String} valuesAs - required
 */
export const fetchSalesByProducts = (payload) => {
  const finalUrl = generatedFinalUrl(payload)
  return axios
    .get(`/authenticated/report/sales/by-products?${finalUrl}`)
    .then((res) => res.data)
}

export const fetchSalesByProductsAsExcel = (payload) => {
  const finalUrl = generatedFinalUrl(payload)
  return axios
    .get(`/authenticated/report/sales/by-products/excel?${finalUrl}`, {
      responseType: 'blob'
    })
    .then((res) => res.data)
}

/**
 * @desc Fetches Sales By Retailers report data
 * @param {String} aggregationPeriod - required
 * @param {Array} bannerGroupIds
 * @param {String} dateFrom - required
 * @param {String} dateTo - required
 * @param {Array} deliveryAddressStates
 * @param {Array} multiStoreOperatorIds
 * @param {Array} storeIds
 * @param {String} valuesAs - required
 */
export const fetchSalesByRetailers = (payload) => {
  const finalUrl = generatedFinalUrl(payload)
  return axios
    .get(`/authenticated/report/sales/by-stores?${finalUrl}`)
    .then((res) => res.data)
}

export const fetchSalesByRetailersAsExcel = (payload) => {
  const finalUrl = generatedFinalUrl(payload)
  return axios
    .get(`/authenticated/report/sales/by-stores/excel?${finalUrl}`, {
      responseType: 'blob'
    })
    .then((res) => res.data)
}

// ***
// Helper functions
// ***

const generatedFinalUrl = (payload) => {
  const {
    aggregationPeriod,
    bannerGroupIds,
    dateFrom,
    dateTo,
    deliveryAddressStates,
    multiStoreOperatorIds,
    storeIds,
    valuesAs
  } = payload

  const baseUrl = `aggregationPeriod=${aggregationPeriod}&dateFrom=${dateFrom}&dateTo=${dateTo}&valuesAs=${valuesAs}`
  let finalUrl = baseUrl

  if (bannerGroupIds?.length > 0)
    finalUrl = `${finalUrl}${constructedArrayUrl(bannerGroupIds, 'bannerGroupIds')}`
  if (multiStoreOperatorIds?.length > 0)
    finalUrl = `${finalUrl}${constructedArrayUrl(multiStoreOperatorIds, 'multiStoreOperatorIds')}`
  if (storeIds?.length > 0)
    finalUrl = `${finalUrl}${constructedArrayUrl(storeIds, 'storeIds')}`
  if (deliveryAddressStates?.length > 0)
    finalUrl = `${finalUrl}${constructedArrayUrl(deliveryAddressStates, 'deliveryAddressStates')}`

  return finalUrl
}

const constructedArrayUrl = (array, propertyName) => {
  let arrayUrl = ''
  array.forEach((element) => {
    arrayUrl += `&${propertyName}=${element}`
  })
  return arrayUrl
}

const extractFilename = (contentDisposition) => {
  if (!contentDisposition) return null
  const match = contentDisposition.match(/filename="?([^";]+)"?/)
  return match?.[1] || null
}
